import type { MapDisplaySettings } from '@/db/types';
import type { BadgeProps } from '@/components/ui/badge';

/**
 * Calculate difficulty multiplier based on map display settings.
 *
 * Higher multiplier = harder quiz
 * - 0.7-0.9: Training Mode (labels on)
 * - 1.0: Default Mode (standard hints)
 * - 1.2-1.4: Intermediate Mode (some hints off)
 * - 1.5-1.7: Hard Mode (most hints off)
 * - 1.8-2.0: Expert Mode (all hints off)
 *
 * @param settings - Map display settings used during quiz
 * @returns Difficulty multiplier (0.7-2.0)
 */
export function calculateDifficultyMultiplier(settings: MapDisplaySettings): number {
  let multiplier = 1.0;

  // Borders off is the biggest difficulty increase
  // Without borders, it's much harder to identify regions
  if (settings.hideOutlines) {
    multiplier += 0.5;
  }

  // Hover highlighting off makes clicking harder
  // Users can't preview which feature they're about to select
  if (settings.disableHover) {
    multiplier += 0.2;
  }

  // Feedback colors off removes learning aid
  // Can't see correct/incorrect visual feedback on map
  if (settings.hideIndicatorColors) {
    multiplier += 0.2;
  }

  // Labels OFF is default behavior (no change)
  // Labels ON makes it EASIER (negative modifier)
  if (!settings.hideLabels) {
    multiplier -= 0.3;
  }

  // Clamp between 0.7 (easiest) and 2.0 (hardest)
  return Math.max(0.7, Math.min(2.0, multiplier));
}

/**
 * Get difficulty level label based on multiplier.
 *
 * @param multiplier - Difficulty multiplier (0.7-2.0)
 * @returns Human-readable difficulty label
 */
export function getDifficultyLabel(multiplier: number): string {
  if (multiplier >= 1.8) return 'Expert Mode';
  if (multiplier >= 1.5) return 'Hard Mode';
  if (multiplier >= 1.2) return 'Intermediate Mode';
  if (multiplier < 1.0) return 'Training Mode';
  return 'Default Mode';
}

/**
 * Get difficulty level short label for badges.
 *
 * @param multiplier - Difficulty multiplier (0.7-2.0)
 * @returns Short difficulty label for compact UI
 */
export function getDifficultyBadgeLabel(multiplier: number): string {
  if (multiplier >= 1.8) return 'Expert';
  if (multiplier >= 1.5) return 'Hard';
  if (multiplier >= 1.2) return 'Intermediate';
  if (multiplier < 1.0) return 'Training';
  return ''; // Default mode doesn't show badge
}

/**
 * Get Badge variant based on difficulty multiplier.
 *
 * @param multiplier - Difficulty multiplier (0.7-2.0)
 * @returns Badge variant for shadcn/ui Badge component
 */
export function getDifficultyBadgeVariant(multiplier: number): BadgeProps['variant'] {
  if (multiplier >= 1.8) return 'destructive';  // Red (Expert)
  if (multiplier >= 1.5) return 'default';      // Primary (Hard)
  if (multiplier >= 1.2) return 'secondary';    // Gray (Intermediate)
  if (multiplier < 1.0) return 'outline';       // Minimal (Training)
  return undefined;                             // No badge (Default)
}

/**
 * Get difficulty color for visual indicators.
 *
 * @param multiplier - Difficulty multiplier (0.7-2.0)
 * @returns Tailwind color class
 */
export function getDifficultyColor(multiplier: number): string {
  if (multiplier >= 1.8) return 'text-red-500';
  if (multiplier >= 1.5) return 'text-orange-500';
  if (multiplier >= 1.2) return 'text-blue-500';
  if (multiplier < 1.0) return 'text-green-500';
  return 'text-gray-500';
}

/**
 * Determine mastery level tier based on difficulty multiplier.
 *
 * @param multiplier - Difficulty multiplier (0.7-2.0)
 * @returns Mastery tier: 'default', 'intermediate', or 'expert'
 */
export function getMasteryTier(multiplier: number): 'default' | 'intermediate' | 'expert' {
  if (multiplier >= 1.5) return 'expert';
  if (multiplier >= 1.2) return 'intermediate';
  return 'default';
}

/**
 * Check if quiz session qualifies for mastery level update.
 * Only perfect scores (100%) count towards mastery.
 *
 * @param accuracyRate - Quiz accuracy rate (0-100)
 * @returns True if session qualifies for mastery
 */
export function qualifiesForMastery(accuracyRate: number): boolean {
  return accuracyRate >= 100;
}
